import { useCallback, useRef, useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";

const initialProductState = {
  serial: null,
  on_hand_net_kg: null,
  statusReason: null,
  selling_unit_of_measure: null,
  on_hand_piece_count: null,
  name: null,
  name_ch: null,
  created_date: null,
  barcode: null,
  barcode_histories: null,
  itemType: null,
  id: null,
};

export function mapBarcodeHistoryProduct(data) {
  const isPallet = data.type === "pallet";
  const item = isPallet ? data.pallet : data.manufactured_item;

  if (!item) throw new Error("Barcode history response is missing its item");

  return {
    itemType: isPallet ? "pallet" : "manufactured",
    id: item.id,
    serial: item.serial,
    plu: item.plu,
    on_hand_net_kg: item.on_hand_net_kg,
    statusReason: item.statusReason,
    selling_unit_of_measure: item.selling_unit_of_measure,
    on_hand_piece_count: item.on_hand_piece_count,
    name: item.product_name,
    name_ch: item.product_name_ch,
    lot_number: item.lot_number,
    created_date: item.created_date,
    lastScanDate: item.lastScanDate,
    barcode: item.barcode,
    barcode_histories: data.barcode_histories,
    warehouse_location_name: item.warehouse_location_name,
    total_cartons: isPallet ? item.total_cartons : 1,
    inv_on_hand_by_plu: item.inv_on_hand_by_plu,
    inv_on_hand_by_lot: item.inv_on_hand_by_lot,
  };
}

export function BarcodeHistoryViewModel(logout) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(initialProductState);
  const latestRequestId = useRef(0);

  const showErrorModal = useCallback((subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  }, []);

  const getBarcodeHistory = useCallback(
    async function (barcode) {
      const requestId = ++latestRequestId.current;
      const normalizedBarcode = barcode.trim();

      if (normalizedBarcode.length === 0) {
        setLoading(false);
        setProduct(initialProductState);
        showErrorModal("Barcode cannot be empty");
        return;
      }

      setLoading(true);
      setProduct(initialProductState);

      try {
        const callResult = await makeGetApiCall(
          `barcodeHistory/${encodeURIComponent(normalizedBarcode)}`
        );

        if (requestId !== latestRequestId.current) return;

        if (!callResult.success) {
          if (callResult.errorStatus === 401) logout();

          showErrorModal(
            callResult.data?.message ?? "We could not load this item's history."
          );
          return;
        }

        setProduct(mapBarcodeHistoryProduct(callResult.data));
      } catch {
        if (requestId !== latestRequestId.current) return;
        showErrorModal("We could not load this item's history.");
      } finally {
        if (requestId === latestRequestId.current) setLoading(false);
      }
    },
    [logout, showErrorModal]
  );

  const deleteManufacturedItemByBarcode = async function (reason) {
    if (!reason || !reason.trim()) {
      showErrorModal("Reason is required");
      return;
    }

    let callResult = await makePostApiCall(
      `deleteManufacturedItemByBarcode/${product.barcode}`,
      { reason: reason.trim() }
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      setProduct(initialProductState);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askDeleteManufacturedItemByBarcode = function () {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={(reason) => deleteManufacturedItemByBarcode(reason)}
          message={`Are you sure you want to delete carton ${product.barcode}?`}
          setOpen={setEmptyModelOpen}
          title="Delete carton"
        />
      ),
      open: true,
    });
  };

  // async function reprintLabel() {
  //   return axios.post("http://192.168.2.248:9100", {
  //     plu: "0108",
  //     name_en: "#1 Cull Slice XL (Bulk)",
  //     name_ch: "筛花皮参片(特大) 散装",
  //     lot_number: "2573647656",
  //     tare_weight: "10.2",
  //     best_before_date: "2025-05-19",
  //     piece_count: "100",
  //     weight_in_kg: "37.82KG",
  //     weight_in_lbs: "79.14LBS",
  //     barcode_str: "0154861755080851112507023102002000215679280005",
  //     label_image_zpl: "",
  //     is_sample: false,
  //     last_9: "000215679",
  //     sample: "",
  //   });
  // }

  const tsplCommand = `
SIZE 100 mm, 150 mm
CLS
TEXT 100,100,"TSS24.BF2",0,2,2,"Hello Vretti"
PRINT 1
`;

  async function reprintLabel() {
    await window.qz.websocket.connect();

    const config = window.qz.configs.create(null);
    config.host = "192.168.2.248";
    config.port = 9100;
    config.useSocket = true;

    await window.qz.print(config, [
      {
        type: "raw",
        format: "plain",
        data: tsplCommand,
      },
    ]);

    await window.qz.websocket.disconnect();
  }

  // const reprintLabel = async function () {
  //   let callResult = await makePostApiCallToPrinter({
  //     plu: "0108",
  //     name_en: "#1 Cull Slice XL (Bulk)",
  //     name_ch: "筛花皮参片(特大) 散装",
  //     lot_number: "2573647656",
  //     tare_weight: "10.2",
  //     best_before_date: "2025-05-19",
  //     piece_count: "100",
  //     weight_in_kg: "37.82KG",
  //     weight_in_lbs: "79.14LBS",
  //     barcode_str: "0154861755080851112507023102002000215679280005",
  //     label_image_zpl: "",
  //     is_sample: false,
  //     last_9: "000215679",
  //     sample: "",
  //   });

  //   if (callResult.success) {
  //   } else {
  //     if (callResult.errorStatus === 401) {
  //       logout();
  //     }
  //     showErrorModal(callResult.data.message);
  //   }
  // };

  return {
    product,
    getBarcodeHistory,
    loading,
    emptyModal,
    setEmptyModelOpen,
    reprintLabel,
    askDeleteManufacturedItemByBarcode,
  };
}
